import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Container,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  connectionAuthorizeUrl,
  deleteConnection,
  listConnections,
  listProviders,
} from '@/api/endpoints';
import type { Connection, EnabledProvider } from '@/api/types';
import { StatusChip } from '@/components/StatusChip';
import { ConfirmTypedDelete } from '@/components/ConfirmTypedDelete';
import { PageHeader } from '@/components/PageHeader';

export function ConnectionsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const connections = useQuery<Connection[]>({
    queryKey: ['connections'],
    queryFn: listConnections,
  });
  const providers = useQuery<EnabledProvider[]>({
    queryKey: ['providers'],
    queryFn: listProviders,
  });

  // Surface OAuth round-trip outcome from query params.
  useEffect(() => {
    const connected = params.get('connected');
    const error = params.get('error');
    if (connected) {
      enqueueSnackbar('Calendar connected.', { variant: 'success' });
      params.delete('connected');
      setParams(params, { replace: true });
    } else if (error) {
      enqueueSnackbar(
        error === 'oauth_failed'
          ? 'Couldn\'t connect that calendar — please try again.'
          : `Connection failed: ${error}`,
        { variant: 'error' },
      );
      params.delete('error');
      setParams(params, { replace: true });
    }
  }, [params, setParams, enqueueSnackbar]);

  const [pickerAnchor, setPickerAnchor] = useState<null | HTMLElement>(null);
  const [pendingDelete, setPendingDelete] = useState<Connection | null>(null);

  const startConnect = (providerConfigId: number) => {
    setPickerAnchor(null);
    window.location.href = connectionAuthorizeUrl(providerConfigId);
  };

  const removeConnection = useMutation({
    mutationFn: (id: number) => deleteConnection(id),
    onSuccess: () => {
      enqueueSnackbar('Connection disconnected.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['connections'] });
      setPendingDelete(null);
    },
    onError: (err) => enqueueSnackbar(err.message, { variant: 'error' }),
  });

  return (
    <Container maxWidth="lg" disableGutters>
      <PageHeader
        title="Connections"
        subtitle="Calendar accounts Cadence is syncing for you."
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(e) => setPickerAnchor(e.currentTarget)}
              disabled={(providers.data?.length ?? 0) === 0}
            >
              Connect calendar
            </Button>
            <Menu
              anchorEl={pickerAnchor}
              open={Boolean(pickerAnchor)}
              onClose={() => setPickerAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {providers.data?.map((p) => (
                <MenuItem key={p.id} onClick={() => startConnect(p.id)}>
                  {p.display_name}
                  <Typography component="span" sx={{ ml: 1 }} variant="caption" color="text.secondary">
                    {p.type_display_name}
                  </Typography>
                </MenuItem>
              ))}
              {(providers.data?.length ?? 0) === 0 && (
                <MenuItem disabled>No providers configured</MenuItem>
              )}
            </Menu>
          </>
        }
      />

      {connections.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {connections.isSuccess && connections.data.length === 0 && (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No connections yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect a calendar account to start mirroring events.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/onboarding')}>
              Get started
            </Button>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {connections.data?.map((c) => (
          <Grid key={c.id} size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap>
                      {c.provider_account_label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {c.provider_display_name}
                    </Typography>
                  </Box>
                  <StatusChip status={c.status} />
                </Stack>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => startConnect(c.provider_config_id)}
                >
                  Reconnect
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setPendingDelete(c)}
                >
                  Disconnect
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ConfirmTypedDelete
        open={pendingDelete !== null}
        title="Disconnect calendar"
        body={
          pendingDelete
            ? `This will delete the connection to ${pendingDelete.provider_account_label}. Any rule that uses one of its calendars will stop syncing.`
            : undefined
        }
        confirmText={pendingDelete?.provider_account_label ?? ''}
        destructiveLabel="Disconnect"
        pending={removeConnection.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => pendingDelete && removeConnection.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
